/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0 desktop flow.
 *
 * Public OAuth clients (desktop, mobile) can't safely embed a client_secret in
 * the binary, so the OAuth spec defines PKCE as an alternative proof: the
 * client generates a random `code_verifier`, derives a `code_challenge`
 * (SHA-256 + base64url) from it, sends the challenge with the authorize
 * request, and proves identity at token-exchange time by sending the
 * original verifier. The authorization server checks SHA-256(verifier) ===
 * stored_challenge.
 *
 * RFC 7636. Used by Clerk OAuth Applications + every modern desktop OAuth
 * flow (Auth0, Google, Microsoft, GitHub).
 */

/** Cryptographically random 43-char URL-safe verifier (RFC 7636 §4.1). */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** SHA-256(verifier), base64url-encoded. Pair with `code_challenge_method=S256`. */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Cryptographically random 32-char CSRF state for the OAuth round-trip. */
export function generateState(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
